/**
 * THE CAR, NEXT TO ITS JOB.
 *
 * Anthony: "a thumbnail of the general auto photo of the car being worked
 * on next to a feed of jobs or quotes. It would really make everything pop."
 *
 * A job number and a name are how the book finds a job. A picture of the
 * car is how the person standing in the bay does — "the white Tacoma" is
 * recognized from across the room, "SC-2026-1824" is not.
 *
 * ─── THIS IS NOT THE ICON-ON-EVERY-ROW THAT icons.ts FORBIDS ───────────
 *
 * Rows stay text; an icon beside every line is decoration. A photo of the
 * actual car is content, and a row with no photo does not get a stock car
 * in its place. It gets the make and year as text, which is what the row
 * already says, set where the eye lands first. That keeps every row the
 * same shape without drawing anything nobody took.
 *
 * ─── WHICH PHOTO ────────────────────────────────────────────────────────
 *
 * The car as it came in, before anything else: `arrival` first, then the
 * earliest `general`, then `release`, then the fault. Never a parts receipt,
 * an inspection sheet or a signature — those are photos OF paper. A job
 * with none of its own borrows the same car's photo from an earlier visit,
 * because it is the same car.
 *
 * ─── A THUMBNAIL, NOT THE PHOTO ─────────────────────────────────────────
 *
 * A job photo is stored at up to 2048px, around 900KB. Forty rows of those
 * is a list that takes a phone thirty seconds on a bay's signal. Each photo
 * has a 144px square stored beside it at about 5KB, made on the phone when
 * it went up, so the list is no heavier with pictures than without. Not
 * storage's render endpoint any more: that is billed per photo and was over
 * the plan on 2026-10-15. If a link fails, the coin stands in — a missing
 * thumbnail must never turn into a broken-image glyph on the main screen.
 *
 * ─── NEVER BLOCKS THE LIST ──────────────────────────────────────────────
 *
 * Rows paint first with the monogram, then `fillThumbs` looks the photos
 * up in one query per screen and swaps them in. A list must not wait on
 * pictures.
 */

import { db } from "../data/client.ts";
import { freshLink, signedThumb, thumbKey } from "../data/photolinks.ts";
import { esc } from "./dom.ts";
import { currentTrade } from "./words.ts";
import { icon } from "./icons.ts";
import { normalizeMake } from "../../public/make-normalize.js";
import { CAR_MODELS } from "../data/car-models.ts";
import { rvArt, rvClassOf } from "../domain/rvclass.ts";
import { carArtUrl } from "../domain/carart.ts";

type Veh = { id?: string | null; year?: number | null; make?: string | null;
  model?: string | null; trim?: string | null; description?: string | null } | null | undefined;

/**
 * A PICTURE OF THAT KIND OF CAR, when nobody has photographed this one.
 *
 * Anthony, 2026-09-24: "we should also have a thumbnail of the image of
 * what the car looks like, if we don't have an image yet ... so that all
 * have images." public/images/cars holds one generated picture per make
 * and model the book has seen at least twice (136 of them, covering about
 * three cars in four). The make goes through the same normalizer the
 * manuals use, so "chevy" and "Chevrolet" find the same picture; the model
 * is its first word with the punctuation taken out, so "F-150" is "f150".
 * Anything else falls through to the coin.
 */
export function modelKey(v: Veh): string | null {
  const make = normalizeMake(v?.make ?? "");
  const model = String(v?.model ?? "").trim().split(/\s+/)[0] ?? "";
  if (!make.make || make.heavyDuty || !model) return null;
  const s = (x: string) => x.toLowerCase().replace(/[^a-z0-9]/g, "");
  const key = `${s(make.make)}-${s(model)}`;
  return CAR_MODELS.has(key) ? key : null;
}

export type CoverCandidate = {
  job_id: string | null;
  vehicle_id: string | null;
  kind: string;
  path: string | null;
  taken_at: string;
};

/** Lower is better. Anything not listed is never a cover. */
const RANK: Record<string, number> = { arrival: 0, general: 1, release: 2, damage: 3 };
export const COVER_KINDS = Object.keys(RANK);

/**
 * The one photo that stands for the car. Best kind first, then the earliest
 * of that kind — the car as it turned up, not halfway into the engine bay.
 */
export function pickCover(photos: CoverCandidate[]): CoverCandidate | null {
  let best: CoverCandidate | null = null;
  for (const p of photos) {
    if (!p.path || !(p.kind in RANK)) continue;
    if (!best) { best = p; continue; }
    const a = RANK[p.kind] as number, b = RANK[best.kind] as number;
    if (a < b || (a === b && p.taken_at < best.taken_at)) best = p;
  }
  return best;
}

/** What a row shows before, or instead of, a photo. */
export function monogram(v: Veh): string {
  const make = shortMake(v?.make);
  if (!make) {
    return `<span class="ct-empty">${icon("vehicles")}</span>`;
  }
  return `<span class="ct-make${make.length > 6 ? " long" : ""}">${esc(make)}</span>${
    v?.year ? `<span class="ct-year">${esc(String(v.year))}</span>` : ""}`;
}

/**
 * The make as it fits in a square. The book holds it however it was
 * typed -- "mercedes benz", "Chevrolet", "chevy" -- so this takes the
 * first word, uses the name a shop says out loud for the long ones, and
 * caps what is left at nine letters. Upper-cased by the stylesheet.
 */
const SAID: Record<string, string> = {
  chevrolet: "Chevy", volkswagen: "VW", "mercedes-benz": "Mercedes",
  "land": "Land Rover", "alfa": "Alfa", mitsubishi: "Mitsu", oldsmobile: "Olds",
};
export function shortMake(raw: string | null | undefined): string {
  const first = (raw ?? "").trim().split(/\s+/)[0] ?? "";
  if (!first) return "";
  const said = SAID[first.toLowerCase()];
  if (said) return said === "Land Rover" ? "Rover" : said;
  return first.slice(0, 9);
}

/**
 * Auto shops only. Anthony, 2026-09-24: "make it auto shops only". The
 * picture is the car, and a lock, a pool or a septic tank is not
 * recognized across a bay the way the white Tacoma is. Any other trade
 * gets no slot at all, so its rows read exactly as they did before.
 *
 * AND RV SHOPS. Anthony, 2026-09-26: "RV photo tiles on mobile." An RV is
 * recognized across a lot the way the white Tacoma is across a bay --
 * the fifth wheel by the pond, the Class A at the end -- so it gets the
 * same slot, with its class standing in until somebody photographs it.
 */
export function showsThumbs(): boolean {
  const t = currentTrade();
  return t === "auto" || t === "rv";
}

/**
 * THE PICTURE OF THAT KIND, or null for the coin.
 *
 * A car finds its make and model in public/images/cars. An RV finds its
 * class, read out of `trim` first because that is where the desk writes
 * it, then the model and the description, because "Jayco Eagle fifth
 * wheel" typed into the model box is somebody telling us. An RV with no
 * class anywhere still gets an RV, never the coin: a coin beside a
 * motorhome says the picture failed, and it did not.
 */
export function standInFor(v: Veh): string | null {
  if (currentTrade() === "rv") return rvArt(rvClassOf(v?.trim, v?.model, v?.description));
  /* Anthony, 2026-10-14: "don't fill with coins ... reuse for similar
     models". A car now always has a picture: its own model, a sibling,
     or its body type. See src/domain/carart.ts. The coin is only what a
     picture that fails to load falls back to. */
  return carArtUrl(v);
}

/** The slot. Paints the monogram now; `fillThumbs` puts the car in later. */
export function thumbHtml(jobId: string, v: Veh, size: "row" | "tile" = "row"): string {
  if (!showsThumbs()) return "";
  const path = cache.get(jobId);
  const known = path ? freshLink(thumbKey(path, THUMB)) : null;
  // Already looked and there is no photo of this car anywhere: the coin
  // straight away, rather than the make and year flashing first.
  const none = cache.has(jobId) && path === null;
  return `<span class="cthumb ct-${size}${known ? " has-photo" : none ? " has-coin" : ""}" aria-hidden="true"
      data-thumb="${esc(jobId)}" data-veh="${esc(v?.id ?? "")}" data-standin="${esc(standInFor(v) ?? "")}">${monogram(v)}${
      known ? `<img src="${esc(known)}" alt="" decoding="async">`
        : none ? `<img src="${esc(standInFor(v) ?? COIN)}" alt="" decoding="async">` : ""}</span>`;
}

/**
 * NO PHOTO OF THE CAR ANYWHERE, SO THE COIN.
 *
 * Anthony, 2026-09-24: "so that all have images ... If you can't figure
 * out a photo, put the most detailed and recent version of the clickflame
 * coin there." Every row carries a picture now.
 *
 * 2026-09-25 Anthony swapped the Clickflame coin for the green and purple
 * Supercanic "Mobile Mechanic, Est. 2024" coin. Cut to its own edge on a
 * real transparent background, at 144px. New filename because /images is
 * cached for a week, so the old URL would keep showing the old coin.
 */
const COIN = "/images/coin-thumb-v4.png";
const coinIn = (s: HTMLElement): void => {
  if (s.querySelector("img")) return;
  const standIn = s.dataset["standin"];
  const img = new Image();
  img.alt = ""; img.decoding = "async";
  img.onload = () => s.classList.add(standIn ? "has-photo" : "has-coin", ...(standIn ? ["is-standin"] : []));
  // A missing stand-in falls to the coin rather than a broken picture.
  img.onerror = () => { if (standIn) { s.dataset["standin"] = ""; img.remove(); coinIn(s); } };
  img.src = standIn || COIN;
  s.appendChild(img);
};

/* jobId -> the cover photo's storage path, or null for "looked, there is
   none". Kept for the session so moving between Jobs, Quotes and the Desk
   does not ask again. The PATH is kept, not a link: the bucket is private
   and a signed link runs out, so it is signed again when it is painted. */
const cache = new Map<string, string | null>();

const THUMB = 144;

const chunk = <T,>(a: T[], n: number): T[][] =>
  Array.from({ length: Math.ceil(a.length / n) }, (_, i) => a.slice(i * n, i * n + n));

const SEL = "job_id,vehicle_id,kind,path,taken_at";

/** Find every unfilled slot under `root` and put its car in. Never throws. */
export async function fillThumbs(root: ParentNode = document): Promise<void> {
  if (!showsThumbs()) return;
  const slots = Array.from(root.querySelectorAll<HTMLElement>(".cthumb[data-thumb]:not(.has-photo)"));
  if (!slots.length) return;

  const want = [...new Set(slots.map((s) => s.dataset["thumb"] as string))]
    .filter((id) => !cache.has(id));

  try {
    if (want.length) {
      const sb = await db();
      const byJob = new Map<string, CoverCandidate[]>();
      for (const ids of chunk(want, 80)) {
        const { data, error } = await sb.from("photos").select(SEL)
          .in("job_id", ids).in("kind", COVER_KINDS).limit(1000);
        if (error) throw new Error(error.message);
        for (const p of (data ?? []) as CoverCandidate[]) {
          const k = p.job_id as string;
          byJob.set(k, [...(byJob.get(k) ?? []), p]);
        }
      }

      // Same car, earlier visit — for jobs that have nothing of their own.
      const vehOf = new Map<string, string>();
      for (const s of slots) {
        const j = s.dataset["thumb"] as string, v = s.dataset["veh"];
        if (v && want.includes(j) && !byJob.has(j)) vehOf.set(j, v);
      }
      const byVeh = new Map<string, CoverCandidate[]>();
      const vids = [...new Set(vehOf.values())];
      for (const ids of chunk(vids, 80)) {
        const { data, error } = await sb.from("photos").select(SEL)
          .in("vehicle_id", ids).in("kind", COVER_KINDS).limit(1000);
        if (error) throw new Error(error.message);
        for (const p of (data ?? []) as CoverCandidate[]) {
          const k = p.vehicle_id as string;
          byVeh.set(k, [...(byVeh.get(k) ?? []), p]);
        }
      }

      for (const id of want) {
        const own = pickCover(byJob.get(id) ?? []);
        const v = vehOf.get(id);
        const pick = own ?? (v ? pickCover(byVeh.get(v) ?? []) : null);
        cache.set(id, pick?.path ?? null);
      }
    }
  } catch {
    // Pictures are the garnish. A failed lookup leaves the monograms, which
    // are already a finished row, and asks again next time the list draws.
    return;
  }

  // One signed link per car, six at a time, so a long list does not fire
  // forty requests at once on a bay's signal.
  const todo = slots.filter((s) => cache.get(s.dataset["thumb"] as string) && !s.querySelector("img"));
  const links = new Map<HTMLElement, string>();
  for (const group of chunk(todo, 6)) {
    await Promise.all(group.map(async (s) => {
      links.set(s, await signedThumb(cache.get(s.dataset["thumb"] as string) as string, THUMB));
    }));
  }

  for (const s of todo) {
    const url = links.get(s);
    if (!url || s.querySelector("img")) continue;
    const img = new Image();
    img.alt = "";
    img.decoding = "async";
    img.onload = () => s.classList.add("has-photo");
    // A link that will not load falls back to the coin. Never a broken-image glyph.
    img.onerror = () => { img.remove(); coinIn(s); };
    img.src = url;
    s.appendChild(img);
  }

  // Looked, and there is no photo of this car on any visit.
  for (const s of slots) {
    const id = s.dataset["thumb"] as string;
    if (cache.has(id) && cache.get(id) === null) coinIn(s);
  }
}

/**
 * A job had a photo added — forget what was remembered about it, so the
 * next list shows the new one rather than the old answer of "none".
 */
export function forgetThumb(jobId: string): void { cache.delete(jobId); }
